import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';

const { FiEdit, FiAlignLeft } = FiIcons;

const AnswerLengthPrediction = ({ questions }) => {
  const [predictions, setPredictions] = useState([]);
  const [lengthDistribution, setLengthDistribution] = useState({
    'Explicit': 0,
    'Very Short': 0,
    'Short': 0,
    'Medium': 0,
    'Long': 0,
    'Extended': 0
  });

  useEffect(() => {
    if (!questions.length) return;
    
    const questionPredictions = questions.map(question => {
      const predictedLength = predictAnswerLength(question.text);
      return {
        id: question.id,
        text: question.text.substring(0, 80) + (question.text.length > 80 ? '...' : ''),
        predictedLength,
        type: question.type || 'Unknown',
        marks: question.marks || 0
      };
    });
    
    setPredictions(questionPredictions);
    
    // Calculate distribution
    const distribution = {
      'Explicit': 0,
      'Very Short': 0,
      'Short': 0,
      'Medium': 0,
      'Long': 0,
      'Extended': 0
    };
    
    questionPredictions.forEach(pred => {
      distribution[pred.predictedLength] = (distribution[pred.predictedLength] || 0) + 1;
    });
    
    setLengthDistribution(distribution);
    
  }, [questions]);
  
  // Function to predict answer length based on question phrasing
  const predictAnswerLength = (text) => {
    const lowerText = text.toLowerCase();
    
    // Check for explicit word count instructions
    if (/\b(\d+)\s*(?:word|words)\b/i.test(lowerText)) {
      return 'Explicit';
    }
    
    // Check for "brief" or "briefly" indicators
    if (/\bbrief|\bbriefly\b/i.test(lowerText)) {
      return 'Short';
    }
    
    // Check for paragraph indicators
    if (/\bparagraph|\bparagraphs\b/i.test(lowerText)) {
      return 'Medium';
    }
    
    // Check for essay indicators
    if (/\bessay|\bin detail\b|\bthoroughly\b/i.test(lowerText)) {
      return 'Long';
    }
    
    // Check for extended response indicators
    if (/\bcomprehensive|\belaborate|\bat length\b/i.test(lowerText)) {
      return 'Extended';
    }
    
    // Check for single word or very short answer indicators
    if (/\bname\b|\blist\b|\bidentify\b|\bwhat is\b|\bwho\b/i.test(lowerText) &&
        !/\bexplain\b|\bdescribe\b|\bdiscuss\b/i.test(lowerText)) {
      return 'Very Short';
    }
    
    // Check for short answer indicators
    if (/\bdescribe\b|\bexplain briefly\b|\bsummarize\b/i.test(lowerText)) {
      return 'Short';
    }
    
    // Check for medium answer indicators
    if (/\bexplain\b|\bdiscuss\b|\banalyze\b|\bcompare\b/i.test(lowerText)) {
      return 'Medium';
    }
    
    // Default based on question type patterns
    if (/multiple choice|true\/false|fill in|fill-in/i.test(lowerText)) {
      return 'Very Short';
    }
    
    // Default to Medium for other cases
    return 'Medium';
  };
  
  // Get appropriate color for length category
  const getLengthColor = (length) => {
    const colorMap = {
      'Explicit': 'bg-blue-100 text-blue-800',
      'Very Short': 'bg-green-100 text-green-800',
      'Short': 'bg-teal-100 text-teal-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Long': 'bg-orange-100 text-orange-800',
      'Extended': 'bg-red-100 text-red-800'
    };
    return colorMap[length] || 'bg-gray-100 text-gray-800';
  };
  
  // Prepare chart options for ECharts
  const getChartOptions = () => {
    const labels = Object.keys(lengthDistribution);
    const data = Object.entries(lengthDistribution).map(([name, value]) => ({ name, value }));
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        data: labels
      },
      series: [
        {
          type: 'pie',
          radius: ['50%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: data,
          color: [
            '#3B82F6', // blue
            '#10B981', // green
            '#14B8A6', // teal
            '#FBBF24', // yellow
            '#F97316', // orange
            '#EF4444'  // red
          ]
        }
      ]
    };
  };
  
  // Length guidelines for reference
  const lengthGuidelines = {
    'Very Short': '1-2 words or a single phrase',
    'Short': '1-3 sentences (20-50 words)',
    'Medium': 'Short paragraph (50-150 words)',
    'Long': 'Multiple paragraphs (150-300 words)',
    'Extended': 'Full essay (300+ words)',
    'Explicit': 'Specifically stated in question'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiEdit} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Answer Length Prediction</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Expected Answer Length Distribution</h4>
          <div className="h-64">
            <ReactECharts
              option={getChartOptions()}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Length Guidelines</h4>
          <div className="space-y-2">
            {Object.entries(lengthGuidelines).map(([length, description]) => (
              <div key={length} className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-lg ${getLengthColor(length)}`}>
                  {length}
                </span>
                <span className="text-sm text-gray-600">{description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        <h4 className="font-medium text-gray-800 mb-4">Sample Questions by Expected Length</h4>
        <div className="space-y-4">
          {Object.keys(lengthDistribution).map(length => {
            const examples = predictions
              .filter(p => p.predictedLength === length)
              .slice(0, 2);
              
            if (examples.length === 0) return null;
            
            return (
              <div key={length} className="border-b border-gray-100 pb-4">
                <div className={`inline-block px-3 py-1 rounded-lg mb-3 ${getLengthColor(length)}`}>
                  {length}
                </div>
                <ul className="space-y-2">
                  {examples.map(example => (
                    <li key={example.id} className="text-sm text-gray-600 flex items-start space-x-2">
                      <SafeIcon icon={FiAlignLeft} className="mt-1 flex-shrink-0" />
                      <div>
                        <p>{example.text}</p>
                        <div className="mt-1 text-xs text-gray-500">
                          {example.type}{example.marks ? ` • ${example.marks} marks` : ''}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-8 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Length-Mark Alignment Recommendation</h4>
        <p className="text-sm text-gray-600 mb-3">
          For fair assessment, align expected answer length with mark allocation:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
          <div className="bg-white p-2 rounded text-center">
            <div className="font-medium mb-1">Very Short</div>
            <div className="text-gray-600">1 mark</div>
          </div>
          <div className="bg-white p-2 rounded text-center">
            <div className="font-medium mb-1">Short</div>
            <div className="text-gray-600">2-3 marks</div>
          </div>
          <div className="bg-white p-2 rounded text-center">
            <div className="font-medium mb-1">Medium</div>
            <div className="text-gray-600">4-6 marks</div>
          </div>
          <div className="bg-white p-2 rounded text-center">
            <div className="font-medium mb-1">Long</div>
            <div className="text-gray-600">7-10 marks</div>
          </div>
          <div className="bg-white p-2 rounded text-center">
            <div className="font-medium mb-1">Extended</div>
            <div className="text-gray-600">10+ marks</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnswerLengthPrediction;